import { logger } from '@/lib/logger';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { FaTwitter, FaLinkedin, FaReddit, FaInstagram } from 'react-icons/fa';
import Ticker from '@/components/Ticker';
import DashboardHero from '@/components/landing/DashboardHero';
import Logo from '@/components/Logo';
import { ThemeToggle } from '@/core/oblique-ui';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import GetDemoModal from '@/components/landing/GetDemoModal';
import CountrySelect from '@/components/shared/CountrySelect';
import NewsletterSection from '@/components/landing/NewsletterSection';

/**
 * Cinematic Landing Page - "The Apple Reveal"
 */
export default function LandingPage() {
    const navigate = useNavigate();
    const { user, userData, loading: authLoading } = useAuth();
    const { theme, setTheme } = useTheme();

    useEffect(() => {
        if (!authLoading && user) {
            const lastPath = localStorage.getItem('oblique_last_path') || userData?.lastVisitedPath || '/crm';
            const destination = (lastPath && lastPath !== '/' && lastPath !== '/hub' && lastPath !== '/login' && lastPath !== '/onboarding')
                ? lastPath
                : '/crm';
            navigate(destination, { replace: true });
        }
    }, [user, userData, authLoading, navigate]);
    const [country, setCountry] = useState('US'); // Default
    const [selectedLang, setSelectedLang] = useState('English');
    const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

    const [openMenu, setOpenMenu] = useState(null); // 'products' | 'solutions' | 'resources' | null
    const [transitioning, setTransitioning] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [hidden, setHidden] = useState(false);

    const LOCALES = {
        'English': 'en',
        'English (US)': 'en',
        'Hindi': 'hi',
        'Arabic': 'ar',
        'Mandarin': 'zh-CN',
        'German': 'de',
        'French': 'fr',
        'Spanish': 'es',
        'Spanish (LA)': 'es',
    };

    useEffect(() => {
        if (!document.getElementById('google-translate-script')) {
            const addScript = document.createElement('script');
            addScript.id = 'google-translate-script';
            addScript.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            document.body.appendChild(addScript);
            window.googleTranslateElementInit = () => {
                new window.google.translate.TranslateElement({
                    pageLanguage: 'en',
                    autoDisplay: false
                }, 'google_translate_element');
            };
        }
    }, []);

    const handleLanguageChange = (lang) => {
        setSelectedLang(lang);
        const code = LOCALES[lang] || 'en';
        setTransitioning(true);

        const select = document.querySelector('.goog-te-combo');
        if (select) {
            select.value = code;
            select.dispatchEvent(new Event('change'));
        }
        setTimeout(() => setTransitioning(false), 800);
    };

    const { scrollY } = useScroll();

    useMotionValueEvent(scrollY, "change", (latest) => {
        const previous = scrollY.getPrevious() || 0;
        if (latest > previous && latest > 150) {
            setHidden(true);
            setOpenMenu(null);
        } else {
            setHidden(false);
        }
        setScrolled(latest > 50);
    });

    return (
        <div className="bg-[var(--bg-page)] text-[var(--text-primary)] min-h-screen w-screen overflow-x-hidden notranslate font-sans">
            {/* Global Smooth Transitions Overlay */}
            <AnimatePresence>
                {transitioning && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-[var(--bg-page)] z-[var(--z-overlay)] flex items-center justify-center"
                    >
                        <motion.div 
                            animate={{ rotate: 360 }} 
                            transition={{ repeat: Infinity, duration: 1, ease: "linear" }} 
                            className="w-10 h-10 border-[3px] border-[var(--accent)] border-t-transparent rounded-full" 
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@100..900&display=swap');
                * { font-family: 'Lexend Deca', sans-serif; }
                .material-symbols-rounded { font-family: 'Material Symbols Rounded' !important; }
                
                /* Hide Google Translate original UI */
                #google_translate_element, .skiptranslate, iframe.goog-te-menu-frame {
                    display: none !important;
                    visibility: hidden !important;
                }
                body { top: 0 !important; }
            `}</style>

            {/* Sticky Nav (Mega-Menu Style) */}
            <motion.nav
                variants={{ visible: { y: 0 }, hidden: { y: "-100%" } }}
                animate={hidden ? "hidden" : "visible"}
                transition={{ type: "spring", stiffness: 260, damping: 20, mass: 1 }}
                onMouseLeave={() => setOpenMenu(null)}
                className={`fixed top-0 left-0 right-0 z-[var(--z-navbar)] bg-[var(--bg-surface)] flex flex-col w-full overflow-visible transition-colors ${
                    (openMenu || isMobileMenuOpen) ? 'border-b-0' : 'border-b border-[var(--border)]'
                }`}
            >
                {/* Utility Bar */}
                <div className="hidden xl:flex relative items-center justify-end gap-4 py-2 px-10 bg-[var(--bg-surface)] text-[var(--text-tertiary)] border-b border-[var(--border)]">
                    
                    {/* Region Selector */}
                    <CountrySelect selected={country} onSelect={setCountry} muted />

                    {/* Language Selector */}
                    <div className="group relative notranslate">
                        <div className="flex items-center gap-1 cursor-pointer py-1 px-2 rounded-md hover:text-[var(--text-primary)] transition-all opacity-80 hover:opacity-100">
                            <ObliqueIcon name="translate" size={13} />
                            <span className="font-medium text-xs">{selectedLang}</span>
                        </div>
                        <div 
                            className={`absolute top-full right-0 mt-4 w-56 border border-[var(--border)] rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden p-6 ${
                                theme === 'dark' ? 'bg-[#161b2e]' : 'bg-white'
                            }`}
                        >
                            {[
                                { region: 'Asia', langs: ['Hindi', 'Arabic', 'Mandarin'] },
                                { region: 'Europe', langs: ['English', 'German', 'French', 'Spanish'] },
                                { region: 'Americas', langs: ['English (US)', 'Spanish (LA)'] }
                            ].map(cont => (
                                <div key={cont.region} className="mb-4">
                                    <div className="text-[10px] font-medium uppercase text-[var(--text-tertiary)] py-0 px-2.5 pb-2 tracking-widest">{cont.region}</div>
                                    <div className="flex flex-col gap-px">
                                        {cont.langs.map(l => (
                                            <div 
                                                key={l} 
                                                onClick={() => handleLanguageChange(l)} 
                                                className={`py-1.5 px-2.5 rounded-lg cursor-pointer text-[13px] font-medium transition-colors whitespace-nowrap hover:bg-[var(--bg-page)] hover:text-[var(--text-primary)] ${
                                                    selectedLang === l ? 'text-[#6366f1]' : 'text-[var(--text-secondary)]'
                                                }`}
                                            >
                                                {l}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div 
                        className="cursor-pointer flex items-center gap-1 py-1 px-2 rounded-md font-medium text-xs hover:text-[var(--text-primary)] transition-all opacity-80 hover:opacity-100" 
                        onClick={() => setTheme(theme === 'high-contrast' ? 'light' : 'high-contrast')}
                    >
                        <ObliqueIcon name="contrast" size={13} />
                        High Contrast
                    </div>
                    
                    <div 
                        className="cursor-pointer flex items-center gap-1 py-1 px-2 rounded-md font-medium text-xs hover:text-[var(--text-primary)] transition-all opacity-80 hover:opacity-100" 
                        onClick={() => navigate('/contact')}
                    >
                        <ObliqueIcon name="help_outline" size={13} />
                        Support
                    </div>
                    
                    <div 
                        className="cursor-pointer flex items-center gap-1 py-1 px-2 rounded-md font-medium text-xs hover:text-[var(--text-primary)] transition-all opacity-80 hover:opacity-100" 
                        onClick={() => navigate('/login')}
                    >
                        <ObliqueIcon name="person" size={13} />
                        Login
                    </div>
                </div>

                <div className="flex items-center justify-between py-4 px-6 w-full relative">
                    <div className="flex items-center gap-10">
                        <div onClick={() => navigate('/')} className="cursor-pointer">
                            <Logo size={120} />
                        </div>

                        {/* Desktop Menu */}
                        <div className="hidden lg:flex gap-8">
                            {/* Products - Hover Toggle */}
                            <div
                                onMouseEnter={() => setOpenMenu('products')}
                                onClick={() => setOpenMenu(openMenu === 'products' ? null : 'products')}
                                className={`relative cursor-pointer flex items-center gap-1.5 text-sm font-medium py-1 transition-colors ${
                                    openMenu === 'products' ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]'
                                }`}
                            >
                                Products
                                <motion.svg animate={{ rotate: openMenu === 'products' ? 180 : 0 }} transition={{ duration: 0.25 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                    <polyline points="6 9 12 15 18 9" />
                                </motion.svg>
                            </div>

                            {/* Solutions - Hover Toggle */}
                            <div
                                onMouseEnter={() => setOpenMenu('solutions')}
                                onClick={() => setOpenMenu(openMenu === 'solutions' ? null : 'solutions')}
                                className={`relative cursor-pointer flex items-center gap-1.5 text-sm font-medium py-1 transition-colors ${
                                    openMenu === 'solutions' ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]'
                                }`}
                            >
                                Solutions
                                <motion.svg animate={{ rotate: openMenu === 'solutions' ? 180 : 0 }} transition={{ duration: 0.25 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                    <polyline points="6 9 12 15 18 9" />
                                </motion.svg>
                            </div>

                            {/* Resources - Hover Toggle */}
                            <div
                                onMouseEnter={() => setOpenMenu('resources')}
                                onClick={() => setOpenMenu(openMenu === 'resources' ? null : 'resources')}
                                className={`relative cursor-pointer flex items-center gap-1.5 text-sm font-medium py-1 transition-colors ${
                                    openMenu === 'resources' ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]'
                                }`}
                            >
                                Resources
                                <motion.svg animate={{ rotate: openMenu === 'resources' ? 180 : 0 }} transition={{ duration: 0.25 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                    <polyline points="6 9 12 15 18 9" />
                                </motion.svg>
                            </div>

                            <a href="#pricing" onMouseEnter={() => setOpenMenu(null)} className="text-sm font-medium text-[var(--text-primary)] no-underline py-1 hover:text-[var(--accent)] transition-colors">Pricing</a>
                        </div>
                    </div>

                    <div className="flex gap-4 items-center">
                        <a 
                            href="/contact" 
                            className="hidden sm:block py-2.5 px-6 bg-transparent border border-[#6366f1] rounded-md text-[#6366f1] text-[13px] font-medium no-underline cursor-pointer transition-all hover:bg-[var(--accent)] hover:text-white"
                        >
                            Contact Sales
                        </a>
                        <button 
                            onClick={() => setIsDemoModalOpen(true)} 
                            className="hidden sm:block py-2.5 px-6 bg-[#6366f1] border-0 rounded-md text-white text-[13px] font-medium cursor-pointer transition-all hover:bg-[#4f46e5]"
                        >
                            Get a demo
                        </button>

                        {/* Hamburger Button */}
                        <motion.button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="lg:hidden p-2 text-[var(--text-primary)]"
                            whileTap={{ scale: 0.9 }}
                        >
                            <ObliqueIcon name={isMobileMenuOpen ? "close" : "menu"} size={28} />
                        </motion.button>
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            style={{ top: scrolled ? '64px' : '112px' }}
                            className="fixed left-0 right-0 bottom-0 bg-[var(--bg-surface)] z-[var(--z-dropdown)] p-10 flex flex-col gap-8 overflow-y-auto"
                        >
                            <div className="flex flex-col gap-6">
                                <div className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-widest">Menu</div>
                                <div onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }} className="text-2xl font-medium cursor-pointer">Products</div>
                                <div onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }} className="text-2xl font-medium cursor-pointer">Solutions</div>
                                <div onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }} className="text-2xl font-medium cursor-pointer">Resources</div>
                                <div onClick={() => { navigate('/#pricing'); setIsMobileMenuOpen(false); }} className="text-2xl font-medium cursor-pointer">Pricing</div>
                            </div>

                            <div className="h-px bg-[var(--border)]" />

                            <div className="flex flex-col gap-6">
                                <div className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-widest">Utility</div>
                                <div className="flex items-center gap-3 text-lg font-medium cursor-pointer" onClick={() => { setTheme(theme === 'high-contrast' ? 'light' : 'high-contrast'); setIsMobileMenuOpen(false); }}>
                                    <ObliqueIcon name="contrast" size={20} />
                                    High Contrast
                                </div>
                                <div className="flex items-center gap-3 text-lg font-medium cursor-pointer" onClick={() => { navigate('/contact'); setIsMobileMenuOpen(false); }}>
                                    <ObliqueIcon name="help_outline" size={20} />
                                    Support
                                </div>
                                <div className="flex items-center gap-3 text-lg font-medium cursor-pointer" onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}>
                                    <ObliqueIcon name="person" size={20} />
                                    Login
                                </div>
                            </div>

                            <div className="mt-auto flex flex-col gap-4">
                                <button 
                                    onClick={() => { setIsDemoModalOpen(true); setIsMobileMenuOpen(false); }} 
                                    className="w-full p-4 rounded-xl bg-[var(--accent)] text-white border-0 font-medium text-base cursor-pointer hover:opacity-90 transition-opacity"
                                >
                                    Get a demo
                                </button>
                                <div className="flex justify-center gap-5 text-[var(--text-tertiary)]">
                                    <FaTwitter size={20} />
                                    <FaLinkedin size={20} />
                                    <FaInstagram size={20} />
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Full-Width Animated Mega Menu Panel */}
                <AnimatePresence>
                    {openMenu === 'products' && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            className="bg-[var(--bg-surface)] border-t border-[var(--border)] shadow-xl overflow-hidden"
                        >
                            <div className="max-w-[1200px] mx-auto p-10 flex gap-15">
                                {/* Left - Title + Overview */}
                                <div className="min-w-[240px]">
                                    <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">The Oblique Platform</h3>
                                    <p className="text-[13px] text-[var(--text-secondary)] leading-normal mb-6">All your ops, quality, and intelligence tools in one suite.</p>
                                    <button onClick={() => { setOpenMenu(null); navigate('/software'); }} className="text-[13px] font-medium text-[#6366f1] bg-transparent border-0 cursor-pointer flex items-center gap-1 p-0 hover:underline">
                                        See overview <ObliqueIcon name="arrow_forward" size={14} />
                                    </button>
                                </div>
                                {/* Right - Product Items with > arrows */}
                                <div className="flex-1 flex flex-col gap-2">
                                    {[
                                        { name: 'Operations Hub', icon: 'groups', color: '#6366f1', desc: 'Streamline revenue workflows, leads, and orders.' },
                                        { name: 'Quality Hub', icon: 'verified', color: '#10b981', desc: 'Subjective QA audits turned into objective data.' },
                                        { name: 'Intelligence Hub', icon: 'analytics', color: '#9333ea', desc: 'Agent performance analytics and deep data.' },
                                        { name: 'Knowledge Hub', icon: 'school', color: '#f59e0b', desc: 'Centralized training and learning management.' },
                                        { name: 'Automations', icon: 'flash_on', color: '#ec4899', desc: 'Connect your tools with AI-powered workflows.' }
                                    ].map(hub => (
                                        <div
                                            key={hub.name}
                                            onClick={() => setOpenMenu(null)}
                                            className="flex items-center gap-4 py-3 px-4 rounded-xl cursor-pointer transition-colors hover:bg-[var(--bg-page)] group/hub"
                                        >
                                            <ObliqueIcon name={hub.icon} size={22} color={hub.color} />
                                            <div className="flex-1">
                                                <div className="text-sm font-medium text-[var(--text-primary)]">{hub.name}</div>
                                            </div>
                                            <ObliqueIcon name="chevron_right" size={16} color="var(--text-tertiary)" className="opacity-0 group-hover/hub:opacity-100 transition-opacity" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Solutions Panel */}
                <AnimatePresence>
                    {openMenu === 'solutions' && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }} 
                            animate={{ opacity: 1, height: 'auto' }} 
                            exit={{ opacity: 0, height: 0 }} 
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            className="bg-[var(--bg-surface)] border-t border-[var(--border)] shadow-xl overflow-hidden"
                        >
                            <div className="max-w-[1200px] mx-auto p-10 flex gap-15">
                                <div className="min-w-[220px]">
                                    <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">Solutions</h3>
                                    <p className="text-[13px] text-[var(--text-secondary)] leading-normal">Discover how Oblique fits your team.</p>
                                </div>
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-10">
                                    <div>
                                        <div className="text-[11px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-4 pb-2 border-b border-[var(--border)]">By Use Case</div>
                                        {['Manage Deals', 'Lead Tracking', 'Scoring & QA', 'Resolve Disputes', 'Cross-hub Sync', 'AI Agents'].map(item => (
                                            <div key={item} onClick={() => setOpenMenu(null)} className="py-2 text-sm font-medium text-[var(--text-primary)] cursor-pointer flex items-center justify-between hover:text-[#6366f1] group/sol transition-colors">
                                                {item} <ObliqueIcon name="chevron_right" size={14} className="opacity-0 group-hover/sol:opacity-100 transition-opacity" color="#6366f1" />
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <div className="text-[11px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-4 pb-2 border-b border-[var(--border)]">By Team Size</div>
                                        {['Startups (1-10)', 'Small Business (10-50)', 'Scaleups (50-200)', 'Enterprise (500+)'].map(item => (
                                            <div key={item} onClick={() => setOpenMenu(null)} className="py-2 text-sm font-medium text-[var(--text-primary)] cursor-pointer flex items-center justify-between hover:text-[#6366f1] group/sol transition-colors">
                                                {item} <ObliqueIcon name="chevron_right" size={14} className="opacity-0 group-hover/sol:opacity-100 transition-opacity" color="#6366f1" />
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <div className="text-[11px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-4 pb-2 border-b border-[var(--border)]">Why Oblique?</div>
                                        <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mb-4">Oblique unifies your CRM, QA, and LMS — no more stitching 15 apps together.</p>
                                        <button onClick={() => { setOpenMenu(null); navigate('/about'); }} className="text-[13px] font-medium text-[#6366f1] bg-transparent border-0 cursor-pointer flex items-center gap-1 p-0 hover:underline">
                                            View Product Tour <ObliqueIcon name="arrow_forward" size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Resources Panel */}
                <AnimatePresence>
                    {openMenu === 'resources' && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }} 
                            animate={{ opacity: 1, height: 'auto' }} 
                            exit={{ opacity: 0, height: 0 }} 
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            className="bg-[var(--bg-surface)] border-t border-[var(--border)] shadow-xl overflow-hidden"
                        >
                            <div className="max-w-[1200px] mx-auto p-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                                <div>
                                    <div className="text-[11px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-4 pb-2 border-b border-[var(--border)]">Featured</div>
                                    {[
                                        { name: 'Product Updates', desc: 'Latest features and platform enhancements.' },
                                        { name: "What's New", desc: 'Discover our newest toolsets and modules.' },
                                        { name: 'Why Choose Oblique?', desc: 'Why 10,000+ teams trust our unified OS.' },
                                        { name: 'Trust & Security', desc: 'Enterprise-grade protection and compliance.' }
                                    ].map(item => (
                                        <div key={item.name} onClick={() => setOpenMenu(null)} className="py-2 cursor-pointer group/res">
                                            <div className="text-sm font-medium text-[var(--text-primary)] group-hover/res:text-[#6366f1] transition-colors">{item.name}</div>
                                            <div className="text-xs text-[var(--text-tertiary)] font-medium">{item.desc}</div>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <div className="text-[11px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-4 pb-2 border-b border-[var(--border)]">Community</div>
                                    {[
                                        { name: 'OBLIQUE Events', desc: 'Local meetups and global conferences.' },
                                        { name: 'Webinars', desc: 'Expert-led sessions on quality and ops.' },
                                        { name: 'Oblique Community', desc: 'Connect with fellow professionals.' },
                                        { name: 'Solutions Partners', desc: 'Certified experts to help you scale.' },
                                        { name: 'Tech Partners', desc: 'Integrations that power your workspace.' }
                                    ].map(item => (
                                        <div key={item.name} onClick={() => setOpenMenu(null)} className="py-2 cursor-pointer group/res">
                                            <div className="text-sm font-medium text-[var(--text-primary)] group-hover/res:text-[#6366f1] transition-colors">{item.name}</div>
                                            <div className="text-xs text-[var(--text-tertiary)] font-medium">{item.desc}</div>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <div className="text-[11px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-4 pb-2 border-b border-[var(--border)]">Education</div>
                                    {[
                                        { name: 'Oblique Academy', desc: 'Master the platform with structured courses.' },
                                        { name: 'Oblique Blogs', desc: 'Insights on revenue protection and quality.' },
                                        { name: 'Free Courses & Certs', desc: 'Get certified in Quality Operations.' },
                                        { name: 'Knowledge Base', desc: 'Documentation and deep-dive guides.' }
                                    ].map(item => (
                                        <div key={item.name} onClick={() => setOpenMenu(null)} className="py-2 cursor-pointer group/res">
                                            <div className="text-sm font-medium text-[var(--text-primary)] group-hover/res:text-[#6366f1] transition-colors">{item.name}</div>
                                            <div className="text-xs text-[var(--text-tertiary)] font-medium">{item.desc}</div>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <div className="text-[11px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-4 pb-2 border-b border-[var(--border)]">Services</div>
                                    {[
                                        { name: 'Onboarding', desc: 'Get your team up and running in days.' },
                                        { name: 'Customer Training', desc: 'Tailored training for your specific workflows.' },
                                        { name: 'Data Migration', desc: 'Seamlessly move your data to Oblique.' },
                                        { name: 'Technical Consulting', desc: 'Architecture and automation advisory.' }
                                    ].map(item => (
                                        <div key={item.name} onClick={() => setOpenMenu(null)} className="py-2 cursor-pointer group/res">
                                            <div className="text-sm font-medium text-[var(--text-primary)] group-hover/res:text-[#6366f1] transition-colors">{item.name}</div>
                                            <div className="text-xs text-[var(--text-tertiary)] font-medium">{item.desc}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.nav>

            {/* Hover-outside overlay to close menu */}
            {openMenu && (
                <div onClick={() => setOpenMenu(null)} onMouseEnter={() => setOpenMenu(null)} className="fixed inset-0 z-[calc(var(--z-dropdown)-1)]" />
            )}

            {/* --- SECTION 1: THE REVEAL --- */}
            <section className="min-h-[140vh] relative flex flex-col items-center">
                <div className="pt-40 text-center z-[var(--z-base)] relative">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                    >
                        <h1 className="text-[clamp(48px,6vw,96px)] font-medium tracking-[-0.03em] leading-[0.95] mb-6 text-[var(--text-primary)]">
                            Business Operating <br /> System.
                        </h1>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 1 }}
                        className="text-[clamp(16px,4vw,20px)] text-[var(--text-secondary)] max-w-[700px] mx-auto mb-15 px-6"
                    >
                        Unified CRM, Analytics, Quality, and Automations. <br />
                        Everything you need to scale your agency, in one workspace.
                    </motion.p>

                    <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.8 }}
                        onClick={() => navigate('/login')}
                        className="py-4 px-10 text-base font-medium bg-[#6366f1] text-white border-0 rounded-md cursor-pointer shadow-[0_4px_20px_rgba(243,115,33,0.4)] mb-10 hover:bg-[#4f46e5] transition-colors"
                    >
                        Get Started for Free
                    </motion.button>

                    {/* Trust Badges */}
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
                        className="flex flex-col items-center gap-3"
                    >
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map(i => (
                                <svg key={i} width="20" height="20" viewBox="0 0 24 24" fill="#6366f1" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                                </svg>
                            ))}
                        </div>
                        <p className="text-sm text-[var(--text-tertiary)] font-medium">
                            Trusted by 10,000+ Quality Teams Worldwide
                        </p>
                    </motion.div>
                </div>

                {/* THE TICKER */}
                <div className="w-full max-w-[1200px] mt-15 mb-6 z-[var(--z-base)]">
                    <Ticker />
                </div>

                {/* THE HARDWARE HERO */}
                <div className="flex-1 w-full relative">
                    <DashboardHero />
                </div>
            </section>

            {/* --- TRUSTED BY SECTION --- */}
            <section className="py-15 px-10 border-b border-[var(--border)] bg-[var(--bg-surface)]">
                <div className="max-w-[1200px] mx-auto text-center">
                    <p className="text-xs font-medium uppercase tracking-[2px] text-[var(--text-tertiary)] mb-8">
                        Empowering Quality Teams at Global Leaders
                    </p>
                    <div className="flex justify-center items-center gap-15 flex-wrap opacity-60 grayscale">
                        {['HubSpot', 'Salesforce', 'Zendesk', 'Intercom', 'Stripe'].map(brand => (
                            <div key={brand} className="text-xl font-medium text-[var(--text-primary)] tracking-tight">
                                {brand}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- CORE FEATURES (ZIG-ZAG LAYOUT) --- */}
            <section className="py-30 px-10 bg-[var(--bg-page)]">
                <div className="max-w-[1100px] mx-auto">

                    {/* Feature 1 */}
                    <div className="flex flex-col md:flex-row items-center gap-20 mb-40">
                        <div className="flex-1">
                            <div className="text-[#6366f1] font-medium mb-4 uppercase text-sm">Real-time Auditing</div>
                            <h2 className="text-[40px] font-medium mb-6 text-[var(--text-primary)] leading-[1.1]">
                                See your quality score, <br />as it happens.
                            </h2>
                            <p className="text-lg text-[var(--text-secondary)] leading-relaxed mb-8">
                                Don't wait for end-of-month reports to find out your team is missing the mark. Our live auditing engine updates your dashboard the second a review is submitted.
                            </p>
                            <ul className="list-none p-0 m-0 flex flex-col gap-4">
                                <li className="flex gap-3 text-base text-[var(--text-primary)] font-medium items-center">
                                    <ObliqueIcon name="check_circle" size={20} color="#6366f1" /> Direct HubSpot & CRM Sync
                                </li>
                                <li className="flex gap-3 text-base text-[var(--text-primary)] font-medium items-center">
                                    <ObliqueIcon name="check_circle" size={20} color="#6366f1" /> AI-Enhanced Transcriptions
                                </li>
                            </ul>
                        </div>
                        <div className="flex-[1.2] bg-[var(--bg-subtle)] rounded-3xl p-10 border border-[var(--border)] shadow-xl relative">
                            <div className="w-full aspect-[16/10] bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] overflow-hidden flex flex-col">
                                {/* Live Audit Animation Preview */}
                                <div className="p-4 border-b border-[var(--border)] flex justify-between items-center bg-[var(--bg-subtle)]">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-[var(--success)]" />
                                        <div className="w-20 h-2.5 bg-[var(--text-tertiary)] rounded opacity-30" />
                                    </div>
                                    <div className="w-10 h-3.5 bg-[var(--success-subtle)] rounded" />
                                </div>
                                <div className="flex-1 p-6 relative">
                                    {/* Abstract Grid / Chart Lines */}
                                    <div className="absolute inset-0 opacity-5 [background-image:linear-gradient(var(--border)_1px,transparent_1px),linear-gradient(90deg,var(--border)_1px,transparent_1px)] [background-size:20px_20px]" />
                                    
                                    <div className="flex flex-col gap-4 relative">
                                        {[85, 92, 78].map((w, i) => (
                                            <div key={i} className="flex gap-4 items-center">
                                                <div className="w-8 h-8 rounded-lg bg-[var(--bg-subtle)]" />
                                                <div className="flex-1 space-y-2">
                                                    <div style={{ width: `${w}%` }} className="h-2.5 rounded-full bg-[var(--bg-muted)]" />
                                                    <div className="w-2/5 h-1.5 rounded-full bg-[var(--bg-subtle)]" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <motion.div 
                                        animate={{ y: [0, -10, 0] }}
                                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                        className="absolute bottom-5 right-5 w-20 h-20 rounded-full bg-[var(--accent-glow)] border border-[var(--accent-subtle)] flex items-center justify-center"
                                    >
                                        <div className="w-10 h-10 rounded-full bg-[var(--accent)] opacity-10" />
                                        <div className="absolute text-[10px] font-medium text-[var(--accent)]">98.2%</div>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Feature 2 */}
                    <div className="flex flex-col md:flex-row-reverse items-center gap-20 mb-30">
                        <div className="flex-1">
                            <div className="text-[var(--blue)] font-medium mb-4 uppercase text-sm">AI Compliance</div>
                            <h2 className="text-[40px] font-medium mb-6 text-[var(--text-primary)] leading-[1.1]">
                                Scalability that doesn't sacrifice detail.
                            </h2>
                            <p className="text-lg text-[var(--text-secondary)] leading-relaxed mb-8">
                                Scale your QA operations without doubling your headcount. Oblique's AI flagging system identifies high-risk interactions so your human auditors can focus on what matters.
                            </p>
                            <button className="flex items-center gap-2 py-3 px-6 bg-transparent border border-[var(--border)] rounded-lg text-[var(--text-primary)] font-medium cursor-pointer hover:bg-[var(--bg-hover)] transition-colors">
                                See AI in Action <ObliqueIcon name="arrow_forward" size={16} />
                            </button>
                        </div>
                        <div className="flex-[1.2] bg-[var(--bg-subtle)] rounded-3xl p-10 border border-[var(--border)] shadow-xl">
                            <div className="w-full aspect-[16/10] bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] p-8 flex flex-col gap-5 relative">
                                <div className="text-xs font-medium text-[var(--accent)] uppercase tracking-widest">AI COMPLIANCE CHECK</div>
                                {[1, 2, 3].map(i => (
                                    <div key={i} className={`flex gap-4 items-center ${i === 2 ? 'opacity-100' : 'opacity-60'}`}>
                                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${i === 2 ? 'bg-[var(--danger-subtle)]' : 'bg-[var(--bg-subtle)]'}`}>
                                            <ObliqueIcon name={i === 2 ? "error" : "verified"} size={20} color={i === 2 ? "var(--danger)" : "var(--success)"} interactive={false} />
                                        </div>
                                        <div className="flex-1">
                                            <div className={`h-2.5 bg-[var(--bg-muted)] rounded-full mb-2 ${i === 2 ? 'w-[70%]' : 'w-[60%]'}`} />
                                            <div className="w-2/5 h-1.5 bg-[var(--bg-subtle)] rounded-full" />
                                        </div>
                                        {i === 2 && (
                                            <div className="py-1 px-2 rounded bg-[var(--danger)] text-white text-[9px] font-medium">FLAGGED</div>
                                        )}
                                    </div>
                                ))}
                                
                                <div className="absolute inset-8 border-2 border-[var(--accent-subtle)] rounded-lg pointer-events-none opacity-30" />
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* --- TESTIMONIALS (Pronto-Style Scrolling Cards) --- */}
            <section className="py-20 bg-[var(--bg-muted)] overflow-hidden">
                <div className="text-center mb-12 px-10">
                    <h2 className="text-4xl font-medium text-[var(--text-primary)] tracking-tight mb-3">Loved by quality-first teams</h2>
                    <p className="text-base text-[var(--text-secondary)]">Real feedback from teams using Oblique every day.</p>
                </div>

                {/* Row 1 — scrolls left */}
                <div className="overflow-hidden mb-4">
                    <div 
                        className="testimonial-marquee flex gap-4 w-max [animation:marquee-left_45s_linear_infinite]"
                    >
                        {[...Array(2)].map((_, setIdx) => [
                            { stars: 5, quote: "The Quality Hub caught a 15% revenue leak in our first week. Our QA scores jumped 22 points — it turned auditing from a checkbox into a growth lever.", name: "Alex Rivera", title: "VP of Operations", company: "GlobalScale Tech", module: "Quality Hub", color: "#10b981" },
                            { stars: 5, quote: "Intelligence Hub's real-time dashboards replaced 6 spreadsheets overnight. We now spot pipeline risks before they become losses.", name: "Priya Mehta", title: "Head of Analytics", company: "NovaBridge Solutions", module: "Intelligence Hub", color: "#6366f1" },
                            { stars: 5, quote: "We onboarded 200 agents onto Knowledge Hub in 3 days. The LMS certifications alone saved us ₹4L/month in external training costs.", name: "Rohan Kapoor", title: "Training Manager", company: "SwiftServe India", module: "Knowledge Hub", color: "#f59e0b" },
                            { stars: 5, quote: "Oblique AI auto-scored 12,000 calls in a single quarter — what used to take our QA team 3 weeks now runs in the background.", name: "Sarah Chen", title: "Director of CX", company: "Apex Contact", module: "Oblique AI", color: "#8b5cf6" },
                            { stars: 4, quote: "Operations Hub unified our deal tracking across 4 regions. Cross-hub sync means no more data silos between sales and QA.", name: "James Okafor", title: "Regional Director", company: "PanAfrica BPO", module: "Operations Hub", color: "#3b82f6" },
                            { stars: 5, quote: "The escalation workflows in Quality Hub saved our dispute resolution time by 60%. Agents actually trust the audit process now.", name: "Ananya Sharma", title: "QA Lead", company: "TechVantage", module: "Quality Hub", color: "#10b981" },
                        ].map((t, i) => (
                            <div key={`r1-${setIdx}-${i}`} className="w-[340px] min-w-[340px] max-w-[340px] bg-[var(--bg-surface)] rounded-2xl border border-[var(--border)] p-6 shrink-0">
                                <div className="flex items-center gap-1 mb-3">
                                    {Array.from({ length: 5 }, (_, s) => <span key={s} className={`text-sm ${s < t.stars ? 'text-amber-500' : 'text-[var(--border)]'}`}>★</span>)}
                                    <span style={{ color: t.color, background: `${t.color}15` }} className="ml-auto text-[11px] font-medium py-0.5 px-2 rounded-full">{t.module}</span>
                                </div>
                                <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mb-4 line-clamp-4">"{t.quote}"</p>
                                <div className="flex items-center gap-2.5">
                                    <div style={{ background: t.color }} className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[13px] font-medium shrink-0">{t.name[0]}</div>
                                    <div>
                                        <div className="text-[13px] font-medium text-[var(--text-primary)]">{t.name}</div>
                                        <div className="text-[11px] text-[var(--text-tertiary)]">{t.title}, {t.company}</div>
                                    </div>
                                </div>
                            </div>
                        )))}
                    </div>
                </div>

                {/* Row 2 — scrolls right */}
                <div className="overflow-hidden">
                    <div 
                        className="testimonial-marquee flex gap-4 w-max [animation:marquee-right_50s_linear_infinite]"
                    >
                        {[...Array(2)].map((_, setIdx) => [
                            { stars: 5, quote: "Automations Hub eliminated 80% of our manual follow-ups. Deal reminders, SLA alerts — it all just runs. Our ops team reclaimed 12 hours a week.", name: "Marcus Webb", title: "Sales Ops Manager", company: "VelocityPeak", module: "Automations", color: "#ec4899" },
                            { stars: 5, quote: "Intelligence Hub's funnel analytics showed us exactly where leads were stalling. We restructured our pipeline and saw a 34% close-rate improvement.", name: "Lina Johansson", title: "Revenue Director", company: "NordStream BPO", module: "Intelligence Hub", color: "#6366f1" },
                            { stars: 5, quote: "Quality Hub's defense workflow let our agents rebut unfair scores with evidence. Morale went up, attrition went down 18%.", name: "David Kimani", title: "Operations Lead", company: "EastAfrica Connect", module: "Quality Hub", color: "#10b981" },
                            { stars: 4, quote: "The Knowledge Hub onboarding paths cut our new-hire ramp time from 6 weeks to 10 days. The certification tracking is a game-changer.", name: "Mei Lin", title: "L&D Head", company: "SinoServe Group", module: "Knowledge Hub", color: "#f59e0b" },
                            { stars: 5, quote: "Oblique AI's predictive scoring flagged 3 at-risk deals worth $400K. We saved all three. That's ROI in the first month.", name: "Tom Ashford", title: "CRO", company: "BrightPath UK", module: "Oblique AI", color: "#8b5cf6" },
                            { stars: 5, quote: "Operations Hub's cross-hub sync means our QA team in Noida and sales in Dubai see the same data in real-time. No more email chains.", name: "Fatima Al-Rashid", title: "VP Global Ops", company: "GulfStar Enterprises", module: "Operations Hub", color: "#3b82f6" },
                        ].map((t, i) => (
                            <div key={`r2-${setIdx}-${i}`} className="w-[340px] min-w-[340px] max-w-[340px] bg-[var(--bg-surface)] rounded-2xl border border-[var(--border)] p-6 shrink-0">
                                <div className="flex items-center gap-1 mb-3">
                                    {Array.from({ length: 5 }, (_, s) => <span key={s} className={`text-sm ${s < t.stars ? 'text-amber-500' : 'text-[var(--border)]'}`}>★</span>)}
                                    <span style={{ color: t.color, background: `${t.color}15` }} className="ml-auto text-[11px] font-medium py-0.5 px-2 rounded-full">{t.module}</span>
                                </div>
                                <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mb-4 line-clamp-4">"{t.quote}"</p>
                                <div className="flex items-center gap-2.5">
                                    <div style={{ background: t.color }} className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[13px] font-medium shrink-0">{t.name[0]}</div>
                                    <div>
                                        <div className="text-[13px] font-medium text-[var(--text-primary)]">{t.name}</div>
                                        <div className="text-[11px] text-[var(--text-tertiary)]">{t.title}, {t.company}</div>
                                    </div>
                                </div>
                            </div>
                        )))}
                    </div>
                </div>
            </section>

            {/* --- SOCIAL PROOF & METRICS --- */}
            <section className="py-20 px-6 bg-[var(--text-primary)] text-[var(--bg-page)] text-center">
                <div className="max-w-[1200px] mx-auto">
                    <h2 className="text-[32px] font-medium mb-15">Learn why 10,000+ companies trust Oblique to protect their pipeline.</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                        <div>
                            <div className="text-6xl font-medium mb-2 text-[#6366f1]">150M+</div>
                            <div className="text-base font-medium opacity-90">Audits Processed</div>
                        </div>
                        <div>
                            <div className="text-6xl font-medium mb-2 text-[var(--blue)]">10k+</div>
                            <div className="text-base font-medium opacity-90">Global QA Teams</div>
                        </div>
                        <div>
                            <div className="text-6xl font-medium mb-2 text-[var(--success)]">$2B+</div>
                            <div className="text-base font-medium opacity-90">Revenue Protected</div>
                        </div>
                        <div>
                            <div className="text-6xl font-medium mb-2 text-white">99.9%</div>
                            <div className="text-base font-medium opacity-90">Platform Uptime</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- SECTION 3: PRICING (4-Tier SaaS) --- */}
            <section id="pricing" className="py-30 px-6 pb-15 text-center bg-[var(--bg-page)]">
                <div className="max-w-[1200px] mx-auto">
                    
                    <h2 className="text-[56px] font-medium mb-4 text-[var(--text-primary)] tracking-[-0.03em]">Plans for every stage.</h2>
                    <p className="text-xl text-[var(--text-secondary)] mb-15">Start free. Scale as you grow. No surprises.</p>

                    {/* Top Row: 3 Plans */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        {[
                            { name: 'Starter', basePrice: 0, period: '/month', desc: 'For individuals exploring Oblique.', badge: null, cta: 'Get Started Free', isRecommended: false, features: ['Up to 5 users', '100 audits/month', 'Basic reports', 'Email support', 'Community access'] },
                            { name: 'Professional', basePrice: 29, period: '/user/month', desc: 'For growing teams that need more.', badge: null, cta: 'Start Free Trial', isRecommended: false, features: ['Up to 25 users', 'Unlimited audits', 'Advanced analytics', 'Priority support', 'HubSpot integration', 'Custom dashboards'] },
                            { name: 'Business', basePrice: 79, period: '/user/month', desc: 'For operations teams at scale.', badge: 'RECOMMENDED', cta: 'Start Free Trial', isRecommended: true, features: ['Unlimited users', 'Unlimited audits', 'AI transcriptions', 'Dedicated CSM', 'SSO & SAML', 'Custom SLAs', 'Geo-adaptive security', 'API access'] }
                        ].map(plan => {
                            const formatPrice = (usd) => {
                                if (usd === 0) return country === 'IN' ? '₹0' : country === 'JP' ? '¥0' : country === 'GB' ? '£0' : country === 'EU' ? '€0' : '$0';
                                if (country === 'IN') return `₹${Math.round(usd * 82)}`;
                                if (country === 'JP') return `¥${Math.round(usd * 150)}`;
                                if (country === 'GB') return `£${Math.round(usd * 0.8)}`;
                                if (country === 'EU') return `€${Math.round(usd * 0.92)}`;
                                if (country === 'SG') return `S$${Math.round(usd * 1.35)}`;
                                if (country === 'AU') return `A$${Math.round(usd * 1.5)}`;
                                if (country === 'AE') return `AED ${Math.round(usd * 3.67)}`;
                                return `$${usd}`;
                            };

                            return (
                                <div 
                                    key={plan.name} 
                                    className={`bg-[var(--bg-surface)] rounded-3xl p-10 px-8 text-left relative flex flex-col ${
                                        plan.isRecommended ? 'border-2 border-[#6366f1]' : 'border border-[var(--border)]'
                                    }`}
                                >
                                    {plan.badge && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#6366f1] text-white py-1 px-4 rounded-full text-[11px] font-medium tracking-widest">
                                            {plan.badge}
                                        </div>
                                    )}
                                    <div className="text-lg font-medium text-[var(--text-primary)] mb-2">{plan.name}</div>
                                    <div className="text-[13px] text-[var(--text-secondary)] mb-6">{plan.desc}</div>
                                    <div className="mb-8">
                                        <span className="text-5xl font-medium text-[var(--text-primary)] tracking-[-0.03em]">{formatPrice(plan.basePrice)}</span>
                                        <span className="text-sm text-[var(--text-tertiary)]">{plan.period}</span>
                                    </div>
                                    <ul className="list-none p-0 m-0 flex flex-col gap-3 mb-8 flex-1">
                                        {plan.features.map(f => (
                                            <li key={f} className="flex items-center gap-2.5 text-sm text-[var(--text-secondary)]">
                                                <ObliqueIcon name="check_circle" size={16} color="#6366f1" /> {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <button 
                                        onClick={() => navigate('/login')} 
                                        className={`w-full py-3.5 rounded-xl text-sm font-medium cursor-pointer transition-all ${
                                            plan.isRecommended 
                                                ? 'bg-[#6366f1] text-white border-0 hover:bg-[#4f46e5]' 
                                                : 'bg-transparent text-[#6366f1] border border-[#6366f1] hover:bg-[#6366f1] hover:text-white'
                                        }`}
                                    >
                                        {plan.cta}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Bottom Row: Enterprise + AI */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-[#0f172a] rounded-3xl p-10 px-8 text-left text-white flex flex-col">
                            <div className="text-lg font-medium mb-2">Enterprise</div>
                            <div className="text-[13px] text-slate-400 mb-6">Custom deployment for 500+ seat organizations.</div>
                            <div className="text-5xl font-medium mb-8 tracking-[-0.03em]">Custom</div>
                            <ul className="list-none p-0 m-0 flex flex-col gap-3 mb-8 flex-1">
                                {['Everything in Business', 'Dedicated infrastructure', 'Custom integrations', 'On-premise option', '24/7 premium support', 'Compliance & auditing'].map(f => (
                                    <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                                        <ObliqueIcon name="check_circle" size={16} color="#6366f1" /> {f}
                                    </li>
                                ))}
                            </ul>
                            <button onClick={() => setIsDemoModalOpen(true)} className="w-full py-3.5 rounded-xl bg-white text-[#0f172a] border-0 text-sm font-medium cursor-pointer hover:bg-slate-100 transition-colors">Contact Sales</button>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-10 px-8 text-left text-white relative overflow-hidden flex flex-col">
                            <div className="absolute top-3 right-4 bg-white/20 py-1 px-3 rounded-full text-[11px] font-medium">NEW</div>
                            <div className="flex items-center gap-2.5 mb-2">
                                <ObliqueIcon name="auto_awesome" size={22} color="white" />
                                <span className="text-lg font-medium">Oblique AI</span>
                            </div>
                            <div className="text-[13px] text-white/70 mb-6">Supercharge your workspace with AI-powered automation.</div>
                            <div className="mb-8">
                                <span className="text-5xl font-medium tracking-[-0.03em]">
                                    {country === 'IN' ? '₹2,999' : country === 'JP' ? '¥5,800' : country === 'GB' ? '£35' : country === 'EU' ? '€38' : '$39'}
                                </span>
                                <span className="text-sm text-white/60">/user/month add-on</span>
                            </div>
                            <ul className="list-none p-0 m-0 flex flex-col gap-3 mb-8 flex-1">
                                {['AI-powered transcriptions', 'Smart audit scoring', 'Predictive pipeline insights', 'Custom AI agents', 'Natural language reports', 'Auto-escalation workflows'].map(f => (
                                    <li key={f} className="flex items-center gap-2.5 text-sm text-white/85">
                                        <ObliqueIcon name="auto_awesome" size={14} color="rgba(255,255,255,0.6)" /> {f}
                                    </li>
                                ))}
                            </ul>
                            <button onClick={() => navigate('/login')} className="w-full py-3.5 rounded-xl bg-white text-indigo-600 border-0 text-sm font-medium cursor-pointer hover:bg-slate-100 transition-colors">Add AI to Your Plan</button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-15 px-6 pb-0 bg-[var(--bg-page)]">
                <NewsletterSection />
            </section>

            <footer className="py-20 px-10 pb-10 border-t border-[var(--border)] bg-[var(--bg-surface)]">
                <div className="max-w-[1200px] mx-auto">

                    {/* Main Columns */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-10 mb-20">

                        {/* Oblique Branding & Socials */}
                        <div className="flex flex-col gap-6 col-span-1">
                            <div><Logo size={160} /></div>
                            <p className="text-[var(--text-tertiary)] text-sm leading-relaxed">
                                Built for Quality First Teams. We turn subjective audits into objective revenue protection data. Do not let compliance be your silent leak.
                            </p>
                            <div className="flex gap-5 items-center">
                                <a href="https://x.com/eleventh_in" target="_blank" rel="noopener noreferrer" className="text-[var(--text-tertiary)] transition-transform duration-200 hover:scale-110 hover:text-[var(--text-primary)]" aria-label="Twitter">
                                    <FaTwitter size={22} />
                                </a>
                                <a href="https://www.linkedin.com/company/eleventh-technologies/" target="_blank" rel="noopener noreferrer" className="text-[var(--text-tertiary)] transition-transform duration-200 hover:scale-110 hover:text-[var(--text-primary)]" aria-label="LinkedIn">
                                    <FaLinkedin size={22} />
                                </a>
                                <a href="https://reddit.com/user/eleventh_in" target="_blank" rel="noopener noreferrer" className="text-[var(--text-tertiary)] transition-transform duration-200 hover:scale-110 hover:text-[var(--text-primary)]" aria-label="Reddit">
                                    <FaReddit size={22} />
                                </a>
                                <a href="https://instagram.com/eleventh.in" target="_blank" rel="noopener noreferrer" className="text-[var(--text-tertiary)] transition-transform duration-200 hover:scale-110 hover:text-[var(--text-primary)]" aria-label="Instagram">
                                    <FaInstagram size={22} />
                                </a>
                            </div>
                        </div>

                        {/* Company */}
                        <div>
                            <h4 className="text-[15px] font-medium text-[var(--text-primary)] mb-5">Company</h4>
                            <ul className="list-none p-0 m-0 flex flex-col gap-3">
                                <li><div onClick={() => { window.scrollTo(0, 0); navigate('/about'); }} className="text-[var(--text-secondary)] text-sm cursor-pointer hover:text-[var(--accent)] transition-colors">About</div></li>
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">Vision</a></li>
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">Careers</a></li>
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">Blogs & Resources</a></li>
                            </ul>
                        </div>

                        {/* Support */}
                        <div>
                            <h4 className="text-[15px] font-medium text-[var(--text-primary)] mb-5">Support</h4>
                            <ul className="list-none p-0 m-0 flex flex-col gap-3">
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">Help Center</a></li>
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">Contact Us</a></li>
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">API Documentation</a></li>
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">System Status</a></li>
                            </ul>
                        </div>

                        {/* Policies */}
                        <div>
                            <h4 className="text-[15px] font-medium text-[var(--text-primary)] mb-5">Policies</h4>
                            <ul className="list-none p-0 m-0 flex flex-col gap-3">
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">Privacy Policy</a></li>
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">Terms of Service</a></li>
                                <li><a href="#" className="text-[var(--text-secondary)] text-sm no-underline hover:text-[var(--accent)] transition-colors">Cookie Policy</a></li>
                            </ul>
                        </div>

                        {/* Contact Us */}
                        <div>
                            <h4 className="text-[15px] font-medium text-[var(--text-primary)] mb-5">Contact Us</h4>
                            <div className="flex flex-col gap-4">
                                <div className="text-[13px] text-[var(--text-secondary)] flex items-start gap-2">
                                    <ObliqueIcon name="mail" size={14} color="var(--accent)" className="mt-0.5 shrink-0" /> oblique@eleventh.tech
                                </div>
                                <div className="text-[13px] text-[var(--text-secondary)] flex items-start gap-2 leading-snug">
                                    <ObliqueIcon name="location_on" size={14} color="var(--accent)" className="mt-0.5 shrink-0" />
                                    <div>
                                        <strong>United States</strong><br />
                                        1200 San Antonio Rd<br />
                                        Palo Alto, CA 94303<br />
                                        <ObliqueIcon name="call" size={10} className="inline mr-1" />+1 (415) 555-0123
                                    </div>
                                </div>
                                <div className="text-[13px] text-[var(--text-secondary)] flex items-start gap-2 leading-snug">
                                    <ObliqueIcon name="location_on" size={14} color="var(--accent)" className="mt-0.5 shrink-0" />
                                    <div>
                                        <strong>India</strong><br />
                                        Sector 62, Noida<br />
                                        Uttar Pradesh 201309<br />
                                        <ObliqueIcon name="call" size={10} className="inline mr-1" />+91 98765 43210
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="pt-8 border-t border-[var(--border)] flex justify-between items-center flex-wrap gap-8">
                        <div className="text-[var(--text-tertiary)] text-[13px]">
                            &copy; {new Date().getFullYear()} Oblique. Developed under Eleventh Technologies.
                        </div>

                        <div className="flex items-center gap-6">
                            <div className="text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-widest">Payment Accepted</div>
                            <div className="flex gap-5 items-center">
                                {[
                                    { name: 'Visa', light: 'visa.svg', dark: 'visa.svg', height: 'h-6' },
                                    { name: 'Mastercard', light: 'mastercard.svg', dark: 'mastercard.svg', height: 'h-6' },
                                    { name: 'Amex', light: 'amex.svg', dark: 'amex-dark.svg', height: 'h-6', invertInDark: true },
                                    { name: 'Diners Club', light: 'diners-club-international.svg', dark: 'diners-club-international.svg', height: 'h-6', noDarkFilter: true },
                                    { name: 'Discover', light: 'discover.svg', dark: 'discover.svg', height: 'h-8' },
                                    { name: 'Paypal', light: 'paypal.svg', dark: 'paypal.svg', height: 'h-8' },
                                    { name: 'Apple Pay', light: 'applepay.svg', dark: 'applepaydark.svg', height: 'h-7', invertInDark: true },
                                    { name: 'Google Pay', light: 'google-pay.svg', dark: 'googlepay-black.svg', height: 'h-7', invertInDark: true },
                                    { name: 'CurrencyBtc', light: 'bitcoin.svg', dark: 'bitcoin.svg', height: 'h-7', noDarkFilter: true }
                                ].map((method) => {
                                    const logo = theme === 'dark' ? method.dark : method.light;
                                    let customFilter = 'var(--payment-logo-filter)';
                                    if (theme === 'dark') {
                                        if (method.invertInDark) customFilter = 'invert(1) contrast(1.5)';
                                        else if (method.noDarkFilter) customFilter = 'none';
                                    }
                                    return (
                                        <div key={method.name} className="flex items-center justify-center">
                                            <img 
                                                src={`/Payments/${logo}`} 
                                                alt={method.name} 
                                                className={`${method.height} w-auto object-contain`} 
                                                style={{ filter: customFilter }} 
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
            <div className="fixed bottom-[30px] left-[30px] z-[var(--z-sticky)]">
                <ThemeToggle />
            </div>
            <GetDemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
            <div id="google_translate_element" className="hidden"></div>
        </div>
    );
}
